import React, { useEffect } from 'react';
import * as vega from 'vega';
import {
  ContinuousHistogram,
  DiscreteHistogram,
  FeatureHistogram,
} from '@models/feature';
import { Spec } from 'vega-typings/types/spec';

export const FeatureDistributionChart: React.FC<{
  histogram: FeatureHistogram;
}> = ({ histogram }) => {
  const elemRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!elemRef.current) {
      return;
    }

    const histogramSpec = ContinuousHistogram.isTypeOf(histogram)
      ? createContinuousHistogramSpec(histogram)
      : createDiscreteHistogramSpec(histogram);

    const view = new vega.View(
      vega.parse({
        width: elemRef.current.clientWidth,
        height: 140,
        padding: 5,
        ...histogramSpec,
      }),
      {
        renderer: 'canvas',
        hover: true,
        container: elemRef.current,
      }
    );
    view.runAsync();
  }, [elemRef]);

  return <div ref={elemRef}></div>;
};

function createContinuousHistogramSpec(histogram: ContinuousHistogram): Spec {
  return {
    signals: [
      {
        name: 'tooltip',
        value: {},
        on: [
          { events: 'rect:mouseover', update: 'datum' },
          { events: 'rect:mouseout', update: '{}' },
        ],
      },
    ],
    data: [
      {
        name: 'table',
        values: histogram.bins,
      },
    ],
    scales: [
      {
        name: 'xscale',
        type: 'linear',
        domain: {
          fields: [
            { data: 'table', field: 'l' },
            { data: 'table', field: 'u' },
          ],
        },
        range: 'width',
        nice: true,
        round: true,
      },
      {
        name: 'yscale',
        domain: { data: 'table', field: 'frequency' },
        nice: true,
        range: 'height',
      },
    ],
    axes: [
      {
        orient: 'bottom',
        scale: 'xscale',
        labelOverlap: 'parity',
      },
      { orient: 'left', scale: 'yscale' },
    ],
    marks: [
      {
        type: 'rect',
        from: { data: 'table' },
        encode: {
          enter: {
            x: { scale: 'xscale', field: 'l' },
            x2: { scale: 'xscale', field: 'u' },
            y: { scale: 'yscale', field: 'frequency' },
            y2: { scale: 'yscale', value: 0 },
          },
          update: {
            fill: { value: '#1976d2' },
          },
        },
      },
      {
        type: 'text',
        encode: {
          enter: {
            align: { value: 'center' },
            baseline: { value: 'bottom' },
            fill: { value: '#333' },
          },
          update: {
            x: {
              scale: 'xscale',
              signal: '(tooltip.u + tooltip.l) / 2',
              band: 0.5,
            },
            y: { scale: 'yscale', signal: 'tooltip.frequency', offset: -2 },
            text: { signal: 'tooltip.frequency' },
            fillOpacity: [
              { test: 'datum === tooltip', value: 0 },
              { value: 1 },
            ],
          },
        },
      },
    ],
  };
}

function createDiscreteHistogramSpec(histogram: DiscreteHistogram): Spec {
  return {
    signals: [
      {
        name: 'tooltip',
        value: {},
        on: [
          { events: 'rect:mouseover', update: 'datum' },
          { events: 'rect:mouseout', update: '{}' },
        ],
      },
    ],
    data: [
      {
        name: 'table',
        values: histogram.bins,
      },
    ],
    scales: [
      {
        name: 'xscale',
        type: 'band',
        domain: { data: 'table', field: 'label' },
        range: 'width',
        round: true,
      },
      {
        name: 'yscale',
        domain: { data: 'table', field: 'frequency' },
        nice: true,
        range: 'height',
      },
    ],
    axes: [
      { orient: 'bottom', scale: 'xscale', labelOverlap: 'parity' },
      { orient: 'left', scale: 'yscale' },
    ],
    marks: [
      {
        type: 'rect',
        from: { data: 'table' },
        encode: {
          enter: {
            x: { scale: 'xscale', field: 'label' },
            width: { scale: 'xscale', band: 1 },
            y: { scale: 'yscale', field: 'frequency' },
            y2: { scale: 'yscale', value: 0 },
          },
          update: {
            fill: { value: '#1976d2' },
          },
        },
      },
      {
        type: 'text',
        encode: {
          enter: {
            align: { value: 'center' },
            baseline: { value: 'bottom' },
            fill: { value: '#333' },
          },
          update: {
            x: { scale: 'xscale', signal: 'tooltip.label', band: 0.5 },
            y: { scale: 'yscale', signal: 'tooltip.frequency', offset: -2 },
            text: { signal: 'tooltip.frequency' },
            fillOpacity: [
              { test: 'datum === tooltip', value: 0 },
              { value: 1 },
            ],
          },
        },
      },
    ],
  };
}
